import Papa from 'papaparse'

import type { BidSheetFileErrorCode, ParsedBidSheetRow } from './types'

type ParseBidSheetCsvResult =
  | { ok: true; rows: ParsedBidSheetRow[] }
  | { ok: false; errorCode: BidSheetFileErrorCode }

/**
 * The header text this file expects to find, resolved by the caller from the
 * exact same locale keys that generate the List View export's actual column
 * headers (Listings.ListView.header.listingId / .bidSubmission) — so the two
 * can never drift out of sync, and this stays correct if the export is ever
 * localized.
 */
type BidSheetHeaders = {
  listingId: string
  bidSubmission: string
  /**
   * Not required to be present in the uploaded file (unlike listingId/
   * bidSubmission below) — a sheet downloaded before this column existed, or
   * from a non-mobile storefront with no manifest lines to roll up, simply
   * has no Per-Unit Bid column at all. Read opportunistically; its absence
   * is never a `missingColumns` error.
   */
  perUnitBid: string
  /** Also optional — same reasoning as `perUnitBid` above. */
  units: string
}

const readErrorResult: ParseBidSheetCsvResult = {
  ok: false,
  errorCode: 'readError',
}

const hasCsvExtension = (fileName: string): boolean =>
  fileName.toLowerCase().endsWith('.csv')

/**
 * Finds the 0-based line index (within a `text.split('\n')` of the whole
 * file, header included) of the physical line whose quote can't be trusted
 * to close cleanly on that same line — the case that makes papaparse treat
 * following content as literal text of that one field, or raise a `Quotes`
 * error outright. Mirrors papaparse's own quoting rules rather than toggling
 * on every `"`: a quote only opens a field when it's that field's first
 * character (right after a delimiter, a newline, or the start of the file),
 * and a quote only closes a field when the very next character is a
 * delimiter, a newline, or EOF. A `"` that fails that closing test (e.g. the
 * inch mark in `12" monitor`) is flagged right there, the same moment
 * papaparse itself raises its `Quotes` error for it — neither keeps hunting
 * for some later `"` that would make the field look balanced, since a
 * field that merely *looks* closed after an invalid interior quote is still
 * a `Quotes` error papaparse already raised once it hit that quote. A `""`
 * pair inside an already-open quote is an escaped literal quote, not a
 * toggle, so it's consumed as a pair. A `"` that appears outside a field's
 * leading position (e.g. `Samsung 55" TV`) never opens quote state at all,
 * matching papaparse reading it as a literal character. This export also
 * guarantees one CSV record per physical line, so a quote still open when
 * the line ends is never a legitimate multi-line field to keep scanning
 * past — it's the unclosed-quote condition itself. Returns `null` once
 * nothing is left open.
 */
const findUnclosedQuoteLine = (text: string): number | null => {
  let inQuote = false
  let atFieldStart = true
  let lineIndex = 0
  let openedOnLine = -1
  for (let i = 0; i < text.length; i++) {
    const char = text[i]
    if (!inQuote) {
      if (char === '"' && atFieldStart) {
        inQuote = true
        openedOnLine = lineIndex
        atFieldStart = false
      } else if (char === ',') {
        atFieldStart = true
      } else if (char === '\n') {
        lineIndex++
        atFieldStart = true
      } else {
        atFieldStart = false
      }
    } else if (char === '"') {
      if (text[i + 1] === '"') {
        i++
        continue
      }
      const next = text[i + 1]
      if (
        next === undefined ||
        next === ',' ||
        next === '\n' ||
        next === '\r'
      ) {
        inQuote = false
      } else {
        // An interior quote that fails the closing test is unclosed right
        // here — papaparse doesn't wait to see if a later quote would
        // otherwise balance the field, so this scanner doesn't either.
        return openedOnLine
      }
    } else if (char === '\n') {
      // One CSV record per physical line is guaranteed for this export, so
      // a quote still open when the line ends can only be this unclosed
      // condition, never a legitimate multi-line field to keep scanning
      // past (e.g. a later row's own trailing inch mark "closing" it).
      return openedOnLine
    }
  }
  return inQuote ? openedOnLine : null
}

/** Prepended to a quote-stripped broken line so the data row it becomes can
 * be found directly by content after Papa.parse, rather than by matching a
 * physical-line number to papaparse's data-row index — an offset that
 * drifts whenever a blank line is skipped or an earlier, validly-quoted
 * field spans several physical lines. Deliberately an implausible real
 * cell value, so an actual buyer upload can't collide with it.
 */
const UNREADABLE_ROW_MARKER = ' __unreadable_bid_sheet_row__ '

/**
 * Isolates every unclosed-quote line so a single bad row no longer swallows
 * the rest of the file. Assumes one CSV record per physical line (true for
 * this export, even when hand-edited), so a bad line's quote marks are
 * stripped outright rather than trying to re-derive where its fields should
 * have ended — the row is always flagged `unreadable` below regardless of
 * what garbled values papaparse reads from it, so only the other rows'
 * boundaries need to come out intact. `findUnclosedQuoteLine` never carries
 * quote state across a line boundary (it returns the moment a `\n` arrives
 * while still inside a quote), so each line can be checked on its own in a
 * single pass rather than re-scanning the whole joined text per bad line.
 */
const isolateUnclosedQuoteLines = (text: string): string => {
  const lines = text.split('\n')
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i] ?? ''
    if (findUnclosedQuoteLine(line) === null) continue
    lines[i] = `${UNREADABLE_ROW_MARKER}${line.replace(/"/g, '')}`
  }
  return lines.join('\n')
}

/**
 * Strips the `UNREADABLE_ROW_MARKER` from whichever field it landed on
 * (always the first field of the line it was prepended to) and reports
 * whether this row carried it at all.
 */
const extractUnreadableRow = (
  row: Record<string, string>
): { row: Record<string, string>; unreadable: boolean } => {
  let unreadable = false
  const cleaned: Record<string, string> = {}
  for (const [key, value] of Object.entries(row)) {
    if (typeof value === 'string' && value.startsWith(UNREADABLE_ROW_MARKER)) {
      unreadable = true
      cleaned[key] = value.slice(UNREADABLE_ROW_MARKER.length)
    } else {
      cleaned[key] = value
    }
  }
  return { row: cleaned, unreadable }
}

// Not the native File.text() — jsdom (this project's Jest environment)
// doesn't implement it, so this project's test suite can't run against it.
const readAsText = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.addEventListener(
      'load',
      () => {
        const result = reader.result
        if (typeof result === 'string') resolve(result)
        else reject(new Error('Unexpected result type'))
      },
      false
    )
    reader.addEventListener(
      'error',
      () => reject(new Error('Error reading file')),
      false
    )
    reader.readAsText(file)
  })

/**
 * Parses an uploaded bid sheet CSV into rows the buyer actually filled in.
 * A row with both Bid Submission and Per-Unit Bid blank is an untouched
 * listing, not an error — it is silently dropped here, matching the
 * After-Upload Report's "only submitted rows" scope (GLOB-4879 Req 10). A
 * row with only Per-Unit Bid filled in (a manifest line, with no lot-level
 * bid of its own) still survives — it's touched, just not at the lot level.
 */
export const parseBidSheetCsv = async (
  file: File,
  headers: BidSheetHeaders
): Promise<ParseBidSheetCsvResult> => {
  if (!hasCsvExtension(file.name)) {
    return { ok: false, errorCode: 'notCsv' }
  }

  let text: string
  try {
    text = await readAsText(file)
  } catch {
    // Scoped to just the FileReader read itself (disk/permission issues,
    // etc.) — not a try/catch around the rest of this function — so an
    // unrelated bug further down can't get mislabeled as a file-read error.
    return readErrorResult
  }

  // An unterminated quote (type 'Quotes') otherwise swallows every following
  // line into one field until EOF, with no way to recover where the real
  // row boundaries were. Isolating each bad line's quote marks up front
  // keeps every other row's boundaries intact for papaparse, so a single
  // malformed row no longer costs the buyer every row after it — the bad
  // line itself is flagged 'unreadable' per-row below, found by its marker
  // rather than by position.
  const sanitizedText = isolateUnclosedQuoteLines(text)

  const { data, meta, errors } = Papa.parse<Record<string, string>>(
    sanitizedText,
    {
      header: true,
      skipEmptyLines: true,
    }
  )

  // Defensive fallback only — isolateUnclosedQuoteLines resolves every
  // unclosed quote it can find assuming one CSV record per physical line.
  // A 'Quotes' error surviving that (multi-line quoted field, or some other
  // shape it didn't anticipate) means row boundaries genuinely can't be
  // trusted, so fall back to rejecting the whole file rather than risk
  // mis-isolating a row that's actually fine. A FieldMismatch (a stray/
  // missing trailing comma) is benign and leaves every row readable, so
  // it's not checked here.
  if (errors.some((error) => error.type === 'Quotes')) {
    return { ok: false, errorCode: 'unreadableRows' }
  }

  if (data.length === 0) {
    return { ok: false, errorCode: 'empty' }
  }

  // Checked against the header row itself (meta.fields), not a data row's
  // own keys — Papa.parse omits a key entirely for any row shorter than the
  // header row (e.g. a trailing blank cell with no comma), which is a data
  // quality issue in that one row, not proof the file lacks the column.
  const fields = meta.fields ?? []
  if (
    !fields.includes(headers.listingId) ||
    !fields.includes(headers.bidSubmission)
  ) {
    return { ok: false, errorCode: 'missingColumns' }
  }

  const rows: ParsedBidSheetRow[] = data
    .map((row, index) => {
      const { row: rawRow, unreadable } = extractUnreadableRow(row)
      return {
        rowNumber: index + 1,
        // A broken line's own fields are whatever papaparse salvaged after
        // its quote marks were stripped — not trustworthy as a listingId or
        // bid amount, so left blank rather than fed into grouping/lookup;
        // `unreadable` is what routes it to its own flagged row below.
        listingId: unreadable ? '' : (rawRow[headers.listingId] ?? '').trim(),
        bidSubmissionRaw: unreadable
          ? ''
          : (rawRow[headers.bidSubmission] ?? '').trim(),
        perUnitBidRaw: unreadable
          ? ''
          : (rawRow[headers.perUnitBid] ?? '').trim(),
        unitsRaw: unreadable ? '' : (rawRow[headers.units] ?? '').trim(),
        rawRow,
        ...(unreadable ? { unreadable: true as const } : {}),
      }
    })
    // A row with only a per-unit entry (no lot-level Bid Submission) is the
    // expected shape for a manifest line — it must survive this filter too,
    // or a lot bid entirely via per-unit lines would vanish before grouping
    // ever sees it. An unreadable row survives unconditionally — it has no
    // Bid Submission or Per-Unit Bid to check, but dropping it here would
    // silently truncate it right back out, the exact defect this isolates.
    .filter(
      (row) =>
        row.unreadable ||
        row.bidSubmissionRaw !== '' ||
        row.perUnitBidRaw !== ''
    )

  if (rows.length === 0) {
    return { ok: false, errorCode: 'empty' }
  }

  return { ok: true, rows }
}
